//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { useAuth } from 'contexts/AuthProvider';
import { useModal } from 'contexts/ModalProvider';
import { useToast } from 'contexts/ToastProvider';
import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createToastConfig } from 'utils/toastConfig';
import { StartCheckState, useCheckApiReady } from './useCheckApiReady';

type ActionCheckId = 'restart' | 'reset';

interface RunActionProgressArgs {
  checkId: ActionCheckId;
  name: string;
  title: ReactNode;
  message: ReactNode;
  totalTime: number;
  /* radial modal 的 icon 版型:HW reset 用 'warning',Event restart/reset 維持置中版型(undefined) */
  type?: 'warning' | 'info' | 'success' | 'error';
  /* 完成後導回登入頁前的延遲(ms):HW reset 為 1 秒,Event restart/reset 為 3 秒 */
  redirectDelay: number;
}

const MAX_RETRY_TIME_MS = 120 * 1000;
const RETRY_INTERVAL_MS = 10 * 1000;
const TOAST_DURATION_MS = 30 * 1000;
const CHECK_ADVANCE_TIME_MS = 10 * 1000;

const getActionStatus = (state: StartCheckState): [ActionCheckId, StartCheckState[ActionCheckId]['status']] | null => {
  const actions: ActionCheckId[] = ['restart', 'reset'];
  for (const action of actions) {
    const status = state?.[action]?.status;
    if (status === 'success' || status === 'error') return [action, status];
  }
  return null;
};

/*
 * restart / reset 共用的進度流程:開啟進度 modal → 輪詢 API-ready → 定論後顯示收尾 modal + toast + 導回登入頁。
 * 由 useSseEventTrigger(Web 觸發)與 useSseHwTrigger(裝置按鈕 reset)各自呼叫一份獨立實例,
 * 兩者狀態(useCheckApiReady)互相隔離,不共用。upgrade 走裝置回報進度,不適用此流程,故不在此涵蓋範圍。
 */
export const useActionProgressFlow = () => {
  const { t } = useTranslation();
  const { addRadialProgressModal } = useModal();
  const { addToast } = useToast();
  const { logout, getCheckApiIsReady } = useAuth();
  const { state, startCheck } = useCheckApiReady();

  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  /* 收尾 modal 需沿用進行中 modal 的 title/message/type,由 runActionProgress 寫入 */
  const endPropsRef = useRef<{ title: ReactNode; message: ReactNode; type?: RunActionProgressArgs['type'] }>({
    title: '',
    message: '',
  });

  const endRadialProgressModal = useCallback(
    (name: string, redirectDelay: number) =>
      addRadialProgressModal({
        open: true,
        name: `${name}End`,
        title: endPropsRef.current.title,
        message: endPropsRef.current.message,
        type: endPropsRef.current.type,
        totalTime: 1000,
        finishFunction: () => {
          setTimeout(() => logout(), redirectDelay);
          return true;
        },
      }),
    [addRadialProgressModal, logout]
  );

  const runActionProgress = useCallback(
    async ({ checkId, name, title, message, totalTime, type, redirectDelay }: RunActionProgressArgs) => {
      endPropsRef.current = { title, message, type };

      addRadialProgressModal({
        open: true,
        name,
        title,
        message,
        type,
        totalTime,
        finishFunction: () => {
          /* 延遲觸發(進度動畫跑完才呼叫),此時 re-render 早已完成,讀 stateRef 安全 */
          const isSuccess = stateRef.current[checkId]?.status === 'success';
          if (isSuccess) setTimeout(() => logout(), redirectDelay);
          return isSuccess;
        },
      });

      await startCheck({
        checkId,
        checkFunction: async () => getCheckApiIsReady(),
        enableRetry: true,
        retryMode: 'time',
        callbackFunction: (checkState: StartCheckState) => {
          /* 同步觸發(startCheck 有定論時立即呼叫),必須讀「參數」而非 stateRef,此刻 re-render 尚未發生,ref 仍是舊值 */
          const actionStatus = getActionStatus(checkState);
          if (!actionStatus) return;
          const [, status] = actionStatus;

          endRadialProgressModal(name, redirectDelay);
          addToast({
            ...createToastConfig(status === 'success', checkId, t),
            duration: TOAST_DURATION_MS,
          });
        },
        totalDuration: totalTime + MAX_RETRY_TIME_MS,
        startCheckAt: totalTime - CHECK_ADVANCE_TIME_MS,
        retryInterval: RETRY_INTERVAL_MS,
      });
    },
    [addRadialProgressModal, addToast, endRadialProgressModal, getCheckApiIsReady, logout, startCheck, t]
  );

  return { runActionProgress };
};
